<script setup>
import {ref} from 'vue';
import useMarkdown from "@/composables/useMarkdown";
import elasticAiConversationsService from "@/services/elastic-ai-conversations.service";
import ElasticChatStartup from "@/views/search/ai/ElasticChatStartup.vue";
import AiChatLoader from "@/components/aiChat/AiChatLoader.vue";
import useAiChat from "@/composables/useAiChat";

const md = useMarkdown();
const content = ref(null);
const questionInput = ref(null);

const roundFormat = {
    question: '',
    answer: '',
    round_id: null,
    follow_up: '',
    parts: [],
};
const params = {
    url: '/chat-stream/get-elastic-ai-response',
    roundFormat,
    content,
    trackingFieldName: 'conversation_id'
}
const {
    question,
    rounds,
    loading,
    systemMessages,
    loaderMessage,
    responseId,
    sendRequest,
} = useAiChat(params);

const handleSend = async () => {
    await sendRequest();
    if (questionInput.value) {
        questionInput.value.textContent = '';
    }
    question.value = '';
};

async function openConversation(id) {
    rounds.value = [];
    const {data} = await elasticAiConversationsService.getConversationData(id);
    rounds.value = data.rounds;
    responseId.value = data.conversation_id;
}

function startChat(startQuestion) {
    question.value = startQuestion;
    sendRequest();
}

defineExpose({
    startChat,
    openConversation
});
</script>

<template>
    <div class="modal-ai-chat__content" ref="content">
        <div class="ai-chat__inner">
            <div class="ai-chat">
                <div
                    v-for="(round, index) in rounds"
                    :key="index"
                    class="ai-chat__inner">
                    <div class="ai-chat__user">
                        {{round.question}}
                    </div>
                    <div class="ai-chat__machine" :class="{'long-div': loading && (index === rounds.length - 1)}">
                        <div v-if="loaderMessage.length && index === rounds.length -1">
                            <div class="ai-loader">
                                <AiChatLoader :message="loaderMessage"/>
                            </div>
                        </div>
                        <template v-if="round.parts.length">
                            <div class="list-result">
                                <div v-for="(part, partIndex) in round.parts" :key="partIndex">
                                    <template v-if="part.type === 'startup'">
                                        <div v-if="part.data">
                                            <ElasticChatStartup
                                                :key="part.id"
                                                :startup="part.data"
                                            />
                                        </div>
                                    </template>
                                    <div v-else v-html="md.render(part.text)"/>
                                </div>
                            </div>
                        </template>
                        <div v-else v-html="md.render(round.answer)"></div>
                    </div>
                    <a v-if="round.round_id" :href="`/startups?ai_discovery=${round.round_id}`" target="_blank" class="open-in-manual-mode">Open results in Manual Mode <span class="open-in-manual-mode__arrow"></span></a>
                    <div v-if="round.follow_up" v-html="md.render(round.follow_up)"></div>
                </div>
            </div>
            <div class="ai-chat__footer">
                <div v-if="rounds.length" class="card--ai__search">
                    <span
                        ref="questionInput"
                        role="textbox"
                        :contenteditable="!loading"
                        v-focus
                        @input="question = $event.target.textContent"
                        @keyup.enter.prevent="handleSend"
                        placeholder="Ask another question"
                        class="card--ai__search__input"
                        :class="{'card--ai__search__input--disabled': loading}"
                    />
                    <button :disabled="loading" class="card--ai__search__button" @click="handleSend"/>
                </div>
                <div class="ai-chat__footer__hidden-button">
                    <b-button v-b-toggle.messages variant="primary">System Messages ({{systemMessages.length}})</b-button>
                    <b-collapse id="messages" class="mt-2">
                        <div>
                            Conversation ID: {{responseId}}
                        </div>
                        <div v-for="(message, index) in systemMessages" :key="index">
                            {{ message }}
                        </div>
                        <a class="link" @click="systemMessages = []">Clear</a>
                    </b-collapse>
                    <br><br>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.long-div {
    min-height: 140px;
}
.open-in-manual-mode {
    display: inline-block;
    margin-top: -27px;
    font-weight: 600;
}
.open-in-manual-mode__arrow {
    display: inline-block;
    width: 16px;
    height: 16px;
    vertical-align: middle;
    background: url('../../../images/icon-diagonal-up-arrow.svg') no-repeat center;
    background-size: contain;
}
.modal-ai-chat__content {
    overflow-y: auto;
    min-height: 0;
    scroll-behavior: smooth;
}
.card--ai__search__input:empty::before {
    content: attr(placeholder);
    color: #999;
    pointer-events: none;
}
.card--ai__search__input--disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
